"use client";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { IBlocks } from "@/types";
import { useState, useId, useRef } from "react";
import { Input } from "@/components/ui/input";
import {
    IconChevronRight,
    IconDeviceFloppy,
    IconPlus,
    IconTable,
    IconTableExport,
    IconTrash,
    IconX,
} from "@tabler/icons-react";
import { Textarea } from "@/components/ui/textarea";
import BlockParent from "@/components/admin/profile/sidebar/options/block/components/pre";
import { Toggle } from "@/components/ui/toggle";
import { Separator } from "@/components/ui/separator";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

interface Props {
    block: IBlocks;
    update: (value: IBlocks) => void;
}
type Metadata = Record<string, string>;

export default function BlockMetadata({ block, update }: Props) {
    // Ref for file input (import)
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [metadata, setMetadata] = useState<Metadata>(block.metadata || {});
    const updateField = (oldKey: string, newKey: string, newValue: string) => {
        const updated = { ...metadata };
        delete updated[oldKey];
        updated[newKey] = newValue;
        setMetadata(updated);
        update({ ...block, metadata: updated });
    };

    const removeField = (key: string) => {
        const updated = { ...metadata };
        delete updated[key];
        setMetadata(updated);
        update({ ...block, metadata: updated });
    };
    const [expand, setExpand] = useState(false);
    // Track selected keys for advanced export
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

    // Handler to update selected keys
    const handleSelectKey = (key: string, checked: boolean) => {
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            checked ? next.add(key) : next.delete(key);
            return next;
        });
    };

    // Export selected metadata as JSON file
    const handleExport = () => {
        const exportData = Object.fromEntries(
            Object.entries(metadata).filter(([k]) => selectedKeys.has(k))
        );
        const blob = new Blob([JSON.stringify(exportData, null, 2)], {
            type: "application/json",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `metadata-export.json`;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }, 0);
    };

    // Import metadata from JSON file
    const handleImportClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const json = JSON.parse(event.target?.result as string);
                if (
                    typeof json === "object" &&
                    json !== null &&
                    Object.values(json).every((v) => typeof v === "string")
                ) {
                    // Merge with existing metadata, new keys overwrite old ones
                    const merged = { ...metadata, ...json };
                    setMetadata(merged);
                    update({ ...block, metadata: merged });
                } else {
                    alert(
                        "Invalid JSON structure. Must be an object with string values."
                    );
                }
            } catch {
                alert("Failed to parse JSON file.");
            }
        };
        reader.readAsText(file);
        // Reset input so same file can be uploaded again if needed
        e.target.value = "";
    };
    return (
        <BlockParent
            title="Metadata"
            inline
            subchildrenHide={!expand}
            subchildren={
                <div className="flex flex-col gap-2">
                    <Label variant="subtoggle">
                        Metadata allows you to add custom fields as key-value
                        pairs, which can be used and called from the API.
                    </Label>

                    <div className="flex flex-col gap-3">
                        <Label variant={"toggle"}>Create</Label>
                        <MetadataFieldAdd
                            metadata={metadata}
                            setMetadata={setMetadata}
                            update={update}
                            block={block}
                        />
                        {metadata && (
                            <>
                                <Separator />
                                {/* Modified rendering for key-value pairs */}
                                <div className="flex flex-col gap-2">
                                    <div className="flex items-center gap-3">
                                        <Label variant={"toggle"}>
                                            Saved fields
                                        </Label>
                                    </div>
                                    {Object.entries(metadata).map(
                                        ([key, value]) => (
                                            <MetadataField
                                                key={key}
                                                fieldKey={key}
                                                value={value}
                                                updateField={updateField}
                                                removeField={removeField}
                                            />
                                        )
                                    )}
                                </div>
                            </>
                        )}
                        <Dialog>
                            <DialogTrigger asChild>
                                <Button variant="outline" className="gap-2">
                                    <IconTable /> Advanced editor
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="w-full max-w-3xl gap-0 p-0 sm:h-3/4 sm:w-full sm:max-w-6xl">
                                <DialogHeader className="sticky top-0 z-50 border-b bg-popover p-3">
                                    <div className="flex items-center gap-2">
                                        <DialogTitle>
                                            Metadata for "{block.name}"
                                        </DialogTitle>

                                        <DialogClose asChild>
                                            <Button
                                                variant="ghost"
                                                size={"icon"}
                                                className="ml-auto"
                                            >
                                                <IconX size={16} />
                                            </Button>
                                        </DialogClose>
                                    </div>
                                </DialogHeader>
                                <div
                                    className={cn(
                                        "flex h-full items-start gap-2 overflow-hidden"
                                    )}
                                >
                                    <div className="flex h-full w-full flex-col gap-6 overflow-auto p-3">
                                        {Object.entries(metadata).map(
                                            ([key, value]) => (
                                                <MetadataFieldAdvanced
                                                    key={key}
                                                    fieldKey={key}
                                                    value={value}
                                                    updateField={updateField}
                                                    removeField={removeField}
                                                    selectedKeys={selectedKeys}
                                                    onSelectKey={
                                                        handleSelectKey
                                                    }
                                                />
                                            )
                                        )}
                                    </div>
                                    <div
                                        className={cn(
                                            "h-full w-1/4 p-3",
                                            "transition-all duration-200"
                                        )}
                                    >
                                        <div className="flex h-full w-full flex-col gap-2 overflow-auto rounded-md bg-popover p-3">
                                            <MetadataFieldAdvancedAdd
                                                metadata={metadata}
                                                setMetadata={setMetadata}
                                                update={update}
                                                block={block}
                                            />
                                            <Separator />
                                            <Button
                                                variant="outline"
                                                className="w-full justify-normal gap-2 bg-muted"
                                                onClick={handleExport}
                                                disabled={
                                                    selectedKeys.size === 0
                                                }
                                            >
                                                <IconTableExport size={16} />
                                                Export
                                            </Button>
                                            <Button
                                                variant="outline"
                                                className="w-full justify-normal gap-2 bg-muted"
                                                onClick={handleImportClick}
                                            >
                                                <IconTable size={16} />
                                                Import
                                            </Button>
                                            <input
                                                type="file"
                                                accept=".json,application/json"
                                                ref={fileInputRef}
                                                style={{ display: "none" }}
                                                onChange={handleFileChange}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </DialogContent>
                        </Dialog>
                    </div>
                </div>
            }
        >
            <Toggle
                className="group/meta h-6 w-6 bg-muted text-muted-foreground data-[state=on]:bg-accent data-[state=on]:text-foreground"
                pressed={expand}
                onPressedChange={setExpand}
            >
                <IconChevronRight
                    size={16}
                    className="transition-all group-data-[state=on]/meta:rotate-90"
                />
            </Toggle>
        </BlockParent>
    );
}
function MetadataFieldAdd({
    block,
    update,
    metadata,
    setMetadata,
}: {
    metadata: Metadata;
    setMetadata: React.Dispatch<React.SetStateAction<Metadata>>;
    update: (value: IBlocks) => void;
    block: IBlocks;
}) {
    const [tempKey, setTempKey] = useState("");
    const [tempValue, setTempValue] = useState("");
    const addField = () => {
        if (!tempKey.trim()) return; // Prevent empty keys
        if (metadata[tempKey]) return; // Prevent duplicate keys
        setMetadata({ ...metadata, [tempKey]: tempValue });
        const updated = { ...metadata };
        delete updated[tempKey]; // Delete the old key if it existed (though check above prevents duplicates for new adds)
        updated[tempKey] = tempValue;
        update({ ...block, metadata: updated });
        setTempKey(""); // Reset inputs
        setTempValue("");
    };
    return (
        <div className="mb-3 flex w-full items-start gap-2">
            <Input
                variant="card"
                placeholder="Key"
                value={tempKey}
                onChange={(e) => setTempKey(e.target.value)}
            />
            <div className="flex w-full items-start gap-2">
                <Textarea
                    variant="card"
                    placeholder="Value"
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    className="h-10 max-h-24 min-h-10 w-full"
                />
                <Button
                    variant="outline"
                    className="h-10 w-10 flex-shrink-0 border-success bg-success/10 p-0 text-foreground hover:bg-success/40"
                    onClick={addField}
                >
                    <IconDeviceFloppy size={16} />
                </Button>
            </div>
        </div>
    );
}
function MetadataField({
    fieldKey,
    value,
    updateField,
    removeField,
}: {
    fieldKey: string;
    value: string;
    updateField: (oldKey: string, newKey: string, newValue: string) => void;
    removeField: (key: string) => void;
}) {
    const [localKey, setLocalKey] = useState(fieldKey);
    const [localValue, setLocalValue] = useState(value);
    // This state helps to track if the current field is being edited
    // and to show appropriate buttons.
    const isEdited = localKey !== fieldKey || localValue !== value;

    const handleSave = () => {
        updateField(fieldKey, localKey, localValue);
    };

    return (
        <div className="flex items-start gap-2">
            <Input
                variant="card"
                placeholder="Key"
                value={localKey}
                onChange={(e) => setLocalKey(e.target.value)}
            />
            <Textarea
                variant="card"
                placeholder="Value"
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                className="h-10 max-h-24 min-h-10 w-full"
            />
            <div className="flex justify-end gap-2">
                {isEdited ? (
                    <Button
                        variant="outline"
                        className="h-10 w-10 flex-shrink-0 border-success bg-success/10 p-0 text-foreground hover:bg-success/40"
                        onClick={handleSave}
                    >
                        <IconDeviceFloppy size={16} />
                    </Button>
                ) : (
                    <Button
                        variant="outline"
                        className="h-10 w-10 flex-shrink-0 border-destructive bg-destructive/10 p-0 text-foreground hover:bg-destructive/40"
                        onClick={() => removeField(fieldKey)}
                    >
                        <IconTrash size={16} />
                    </Button>
                )}
            </div>
        </div>
    );
}

interface MetadataFieldAdvancedProps {
    fieldKey: string;
    value: string;
    updateField: (oldKey: string, newKey: string, newValue: string) => void;
    removeField: (key: string) => void;
    selectedKeys: Set<string>;
    onSelectKey: (key: string, checked: boolean) => void;
}

function MetadataFieldAdvanced({
    fieldKey,
    value,
    updateField,
    removeField,
    selectedKeys,
    onSelectKey,
}: MetadataFieldAdvancedProps) {
    const [localKey, setLocalKey] = useState(fieldKey);
    const [localValue, setLocalValue] = useState(value);
    // This state helps to track if the current field is being edited
    // and to show appropriate buttons.
    const isEdited = localKey !== fieldKey || localValue !== value;

    const handleSave = () => {
        updateField(fieldKey, localKey, localValue);
    };
    const id = useId();
    return (
        <div className="flex flex-col gap-2 rounded-md bg-muted p-2">
            <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                    <Checkbox
                        className="border-accent data-[state=checked]:border-accent data-[state=checked]:text-foreground"
                        checked={selectedKeys.has(fieldKey)}
                        onCheckedChange={(checked) => {
                            // onCheckedChange can be boolean or "indeterminate"; treat indeterminate as false
                            onSelectKey(fieldKey, Boolean(checked));
                        }}
                    />
                    <div className="relative flex w-full items-center gap-1 rounded-md border border-transparent bg-accent shadow-md  outline-none transition-all duration-300 focus-within:border-primary focus-within:ring-0 focus-within:ring-transparent has-[input:is(:disabled)]:*:pointer-events-none">
                        <Label
                            variant={"toggle"}
                            htmlFor={"key-" + id}
                            className="my-1 ml-1 block w-fit rounded-md bg-popover px-3 py-1 font-mono text-sm font-semibold text-foreground"
                        >
                            Key
                        </Label>
                        <Input
                            variant={"card"}
                            id={"key-" + id}
                            className="flex w-full border-0 px-3 py-2 text-foreground focus-visible:border-0 focus-visible:outline-none"
                            value={localKey}
                            onChange={(e) => setLocalKey(e.target.value)}
                        />
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                        {isEdited ? (
                            <Button
                                variant="outline"
                                className="h-10 w-10 flex-shrink-0 border-success bg-success/10 p-0 text-foreground hover:bg-success/40"
                                onClick={handleSave}
                            >
                                <IconDeviceFloppy size={16} />
                            </Button>
                        ) : (
                            <Button
                                variant="outline"
                                className="h-10 w-10 flex-shrink-0 border-destructive bg-destructive/10 p-0 text-foreground hover:bg-destructive/40"
                                onClick={() => removeField(fieldKey)}
                            >
                                <IconTrash size={16} />
                            </Button>
                        )}
                    </div>
                </div>
                <div className="relative rounded-md border border-transparent bg-accent shadow-md outline-none  transition-all duration-300 focus-within:border-primary focus-within:ring-0 focus-within:ring-transparent has-[input:is(:disabled)]:*:pointer-events-none">
                    <Label
                        variant={"toggle"}
                        htmlFor={"value-" + id}
                        className="ml-1 mt-1 block w-fit rounded-md bg-popover px-3 py-1 font-mono text-sm font-semibold text-foreground"
                    >
                        Value
                    </Label>
                    <Textarea
                        variant={"card"}
                        id={"value-" + id}
                        className="flex min-h-[70px] w-full border-0 px-3 pb-2 text-foreground focus-visible:border-0 focus-visible:outline-none"
                        value={localValue}
                        onChange={(e) => setLocalValue(e.target.value)}
                    />
                </div>
            </div>
        </div>
    );
}
function MetadataFieldAdvancedAdd({
    block,
    update,
    metadata,
    setMetadata,
}: {
    metadata: Metadata;
    setMetadata: React.Dispatch<React.SetStateAction<Metadata>>;
    update: (value: IBlocks) => void;
    block: IBlocks;
}) {
    const [tempKey, setTempKey] = useState("");
    const [tempValue, setTempValue] = useState("");
    const addField = () => {
        if (!tempKey.trim()) return; // Prevent empty keys
        if (metadata[tempKey]) return; // Prevent duplicate keys
        setMetadata({ ...metadata, [tempKey]: tempValue });
        const updated = { ...metadata };
        delete updated[tempKey]; // Delete the old key if it existed (though check above prevents duplicates for new adds)
        updated[tempKey] = tempValue;
        update({ ...block, metadata: updated });
        setTempKey(""); // Reset inputs
        setTempValue("");
    };
    const [advancedAddOpen, setAdvancedAddOpen] = useState(false);

    return (
        <Popover open={advancedAddOpen} onOpenChange={setAdvancedAddOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    className="w-full justify-normal gap-2 bg-transparent"
                >
                    <IconPlus size={16} />
                    Add new
                </Button>
            </PopoverTrigger>
            <PopoverContent className="z-[9999999] p-0">
                <div className="flex flex-col gap-2 rounded-md bg-muted p-2">
                    <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-2">
                            <div className="relative flex w-full items-center gap-1 rounded-md border border-transparent bg-accent shadow-md  outline-none transition-all duration-300 focus-within:border-primary focus-within:ring-0 focus-within:ring-transparent has-[input:is(:disabled)]:*:pointer-events-none">
                                <Label
                                    variant={"toggle"}
                                    className="my-1 ml-1 block w-fit rounded-md bg-popover px-3 py-1 font-mono text-sm font-semibold text-foreground"
                                >
                                    Key
                                </Label>
                                <Input
                                    variant={"card"}
                                    className="flex w-full border-0 px-3 py-2 text-foreground focus-visible:border-0 focus-visible:outline-none"
                                    value={tempKey}
                                    onChange={(e) => setTempKey(e.target.value)}
                                />
                            </div>
                            <div className="ml-auto flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    className="h-10 w-10 flex-shrink-0 border-success bg-success/10 p-0 text-foreground hover:bg-success/40"
                                    onClick={() => {
                                        addField();
                                        setAdvancedAddOpen(false);
                                    }}
                                >
                                    <IconDeviceFloppy size={16} />
                                </Button>
                            </div>
                        </div>
                        <div className="relative rounded-md border border-transparent bg-accent shadow-md outline-none  transition-all duration-300 focus-within:border-primary focus-within:ring-0 focus-within:ring-transparent has-[input:is(:disabled)]:*:pointer-events-none">
                            <Label
                                variant={"toggle"}
                                className="ml-1 mt-1 block w-fit rounded-md bg-popover px-3 py-1 font-mono text-sm font-semibold text-foreground"
                            >
                                Value
                            </Label>
                            <Textarea
                                variant={"card"}
                                className="flex min-h-[70px] w-full border-0 px-3 pb-2 text-foreground focus-visible:border-0 focus-visible:outline-none"
                                value={tempValue}
                                onChange={(e) => setTempValue(e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}
